import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { Eyebrow, PageHeader } from "../components/bits";
import RoiCalculator from "../components/poc/RoiCalculator";
import { IMG, SEGMENTS } from "../data/content";

export default function Business() {
  return (
    <Layout>
      <PageHeader
        eyebrow="For business"
        title="&ldquo;Always charged&rdquo; is the new free Wi-Fi."
        subtitle="Guests sit down; their devices charge. No cable lockers, no outlet hunting, no borrowed bricks. ChargeRevo turns every table, desk, and dock into an amenity your competitors can't match."
      />

      {/* HOSPITALITY */}
      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-navy/10">
              <img src={IMG.heroHotel} alt="Hotel lobby with ChargeRevo charging tables" className="w-full" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow dark>Hospitality &amp; coworking</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">An amenity guests feel instantly.</h2>
            <p className="mt-5 text-lg leading-relaxed text-navy/70">
              Revo Venue turns every table and nightstand into an amenity your competitors can&apos;t match — with guest passes, member roaming, and analytics on how people actually use your spaces.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ENTERPRISE */}
      <section className="bg-navy px-5 py-20 md:px-8">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:[&>*:first-child]:order-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <img src={IMG.warehouseAisle} alt="Warehouse scanners charging on ChargeRevo" className="w-full" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Enterprise IoT, warehouse &amp; healthcare</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Kill the battery-swap shift.</h2>
            <p className="mt-5 text-lg leading-relaxed text-revo-ice">
              Your teams spend real payroll finding, swapping, and staging batteries for scanners, trackers, and sensors. Revo Enterprise blankets staging areas, cabinets, and docks with entitlement-gated power — every device charges automatically, every watt-hour is metered to its serial, and battery-replacement labor drops toward zero.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TIER CLARITY */}
      <section className="bg-navy px-5 pb-4 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="grid gap-4 sm:grid-cols-3" data-testid="business-tier-clarity">
              {[
                { name: "Revo Pro", sub: "For your team.", body: "Covers your own employees' devices across offices and studios. A monthly fee per room zone — pricing revealed at launch." },
                { name: "Revo Venue", sub: "For your guests.", body: "Adds guest day-passes, roaming settlement, and visitor analytics for people who aren't on your account. Pricing revealed at launch." },
                { name: "Revo Enterprise", sub: "For your fleet.", body: "Managed deployment and per-serial metering for device fleets, priced against battery-swap labor savings. Pricing revealed at launch." },
              ].map((t) => (
                <div key={t.name} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                  <h3 className="font-display text-lg font-extrabold text-white">{t.name}</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-revo-green">{t.sub}</p>
                  <p className="mt-3 text-sm leading-relaxed text-revo-ice/70">{t.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-center text-sm text-revo-ice/60" data-testid="business-tier-note">
              In short: <span className="font-semibold text-white">Pro charges your team; Venue charges everyone else&apos;s.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ROI POC */}
      <section className="bg-navy px-5 pb-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal className="mb-10 text-center">
            <Eyebrow>Calculate it</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">See your payback in months, not years.</h2>
          </Reveal>
          <Reveal delay={0.1}><RoiCalculator /></Reveal>
        </div>
      </section>

      {/* SEGMENT GALLERY */}
      <section className="bg-white px-5 py-24 md:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow dark>Where ChargeRevo works</Eyebrow>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Every space people bring devices into.</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SEGMENTS.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.1}>
                <div className="group h-full overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface" data-testid={`segment-${s.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={IMG[s.img]} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-extrabold text-navy">{s.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy/60">{s.blurb}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OEM */}
      <section className="bg-white px-5 pb-24 md:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <Eyebrow dark>Revo Inside</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-5xl">OEM &amp; builder partners.</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-navy/70">
              Laptop and appliance OEMs: embed the receiver, ship a subscription-attached SKU, and earn recurring revenue with us. Builders and kitchen remodelers: offer Revo-ready desks and Ki counters as a premium line. Reference designs, certification support, and co-marketing included.
            </p>
            <Link to="/join" data-testid="partner-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-bold text-white transition-transform duration-200 hover:scale-105">
              Partner with ChargeRevo <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
